import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import Home from './Home';
import OrderSuccess from './OrderSuccess';
import OrderFailure from './OrderFailure';
import OrderTracker from './components/OrderTracker';
import OrdersHistory from './components/OrdersHistory';
import { Toaster } from 'sonner';

// ── Code-splitting ────────────────────────────────────────
// Админ/кассир-роуты грузятся отдельными чанками только когда
// сотрудник их открывает. Гостю (99% трафика) эти ~800KB не нужны.
const Login = lazy(() => import('./components/Admin/Login'));
const Dashboard = lazy(() => import('./components/Admin/Dashboard'));
const TableManager = lazy(() => import('./components/Admin/TableManager'));
const StaffPinManager = lazy(() => import('./components/Admin/StaffPinManager'));
const StaffManager = lazy(() => import('./components/Admin/StaffManager'));
const Analytics = lazy(() => import('./components/Admin/Analytics'));
const OrdersManager = lazy(() => import('./components/Admin/OrdersManager'));
const AuditLog = lazy(() => import('./components/Admin/AuditLog'));
const PromoManager = lazy(() => import('./components/Admin/PromoManager'));
const SettingsPage = lazy(() => import('./components/Admin/SettingsPage'));
const AdminLayout = lazy(() => import('./components/Admin/AdminLayout'));
const ProtectedRoute = lazy(() => import('./components/Admin/ProtectedRoute'));
const CashierDashboard = lazy(() => import('./components/Cashier/CashierDashboard'));

const RouteSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-surface">
    <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const App: React.FC = () => {
  return (
    // reducedMotion="user" — все framer-motion анимации (23 файла) уважают
    // системную настройку «Уменьшить движение»: transform/layout-переходы
    // отключаются, opacity сохраняется. CSS-анимации гасит media-query в index.html.
    <MotionConfig reducedMotion="user">
      <Suspense fallback={<RouteSpinner />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/table/:tableId" element={<Home />} />
          <Route path="/takeaway" element={<Home />} />
          <Route path="/admin/login" element={<Login />} />
          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/order-failure" element={<OrderFailure />} />
          <Route path="/order/:id" element={<OrderTracker />} />
          <Route path="/orders" element={<OrdersHistory />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<Dashboard />} />
              <Route path="/admin/tables" element={<TableManager />} />
              <Route path="/admin/pins" element={<StaffPinManager />} />
              <Route path="/admin/staff" element={<StaffManager />} />
              <Route path="/admin/analytics" element={<Analytics />} />
              <Route path="/admin/orders" element={<OrdersManager />} />
              <Route path="/admin/audit" element={<AuditLog />} />
              <Route path="/admin/promo" element={<PromoManager />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/cashier" element={<CashierDashboard />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      <Toaster
        position="top-center"
        richColors
        // Брендовая тема. sonner toggle'ит между light/dark по data-theme на <html>,
        // мы передаём 'system' чтобы sonner следил сам — и совпадал с
        // переключателем темы в админке/гостевом меню.
        theme="system"
        toastOptions={{
          style: {
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--glass-border)',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgb(0 0 0 / 0.08)',
            fontWeight: 600,
          },
          className: 'font-sans',
        }}
      />
    </MotionConfig>
  );
};

export default App;
